import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAdFreeSubscriber } from '../contexts/AdFreeSubscriberContext';
import { languageContent } from '../utils/languageContent';
import { isNativeMobile } from '../utils/adMobService';

/**
 * Banner ad shown at the top of each page.
 * - On native Android/iOS: returns null — the native AdMob banner is managed
 *   globally by BannerAdManager (route-aware, no per-page mount/unmount cycles).
 * - On web: renders a Google AdSense ins slot inline.
 */
const LessonBannerAd: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const { isPremiumSubscriber } = useAdFreeSubscriber();
  const content = languageContent[currentLanguage];
  const insRef = useRef<HTMLModElement>(null);
  const pushedRef = useRef(false);

  useEffect(() => {
    if (isNativeMobile() || isPremiumSubscriber) return;

    const pushWhenSized = () => {
      const el = insRef.current;
      if (!el || pushedRef.current) return;
      // Wait until layout gives the slot a real width (avoids AdSense "availableWidth=0")
      if (el.offsetWidth < 50) {
        requestAnimationFrame(pushWhenSized);
        return;
      }
      pushedRef.current = true;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).adsbygoogle = (window as any).adsbygoogle || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).adsbygoogle.push({});
      } catch (e) {
        pushedRef.current = false;
        console.warn('AdSense error', e);
      }
    };

    // Double rAF: after paint + layout
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(pushWhenSized);
    });
    return () => cancelAnimationFrame(id);
  }, [isPremiumSubscriber]);

  // On native, the banner is managed by BannerAdManager — nothing to render here
  if (isNativeMobile() || isPremiumSubscriber) return null;

  // Web: render the AdSense slot inline
  return (
    <div className="lesson-banner-ad">
      <span className="lesson-banner-ad-label">{content.adBannerText}</span>
      <div className="lesson-banner-ad-slot">
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-client="ca-pub-7754802589108768"
          data-ad-slot="3522444311"
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>
    </div>
  );
};

export default LessonBannerAd;
